import {
  assetContentTag,
  fetchQuoteAsset,
  getV6FrameForContainer,
  getV6QuoteItemForContainer,
  isV6
} from '../../quotes/data/v6/helper-functions';
import { css, html, nothing, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { EventTemplate } from '../../../webmodule-common/other/ui/events';
import { FabricationJobContainerManager } from '../data/fabrication-job-container';
import { FabricationJobItem, FabricationJobState, QuoteItemFabricationStatus } from '../../api/fabrication-job-api';
import { FactoryTrackingReferenceDialog } from './factory-tracking-reference-dialog';
import { fireQuickErrorToast } from '../../../webmodule-common/other/toast-away';
import { frameExpanderStyles } from '../../frames/frame-expander-styles';
import { frameValidationIcon, getFrameDetailsTemplate } from '../../frames/ui';
import { FrameValidationType } from '../../../webmodule-common/interop/webmodule-interop';
import { getApiFactory } from '../../api/api-injector';
import { getQuoteItemFabricationStatusIcon } from '../data/fabrication-job-helper-functions';
import { getV6QuoteItemSubtitleOrFrameDimensions } from '../../v6config/v6-ui';
import { highestValidationRank, rankToValidationType } from '../../v6config/validation-rank';
import { inCellBadgeLinkStyles } from '../../../webmodule-common/other/ui/in-cell-badge-link-styles';
import { interpolateExternal } from '../../../localization/global';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { isFrame, quoteItemThumbSrc } from '../../quotes/data/quote-helper-functions';
import { isSameDeliveryBatch } from '../data/delivery-batch';
import { isValidV6ConfigVersion } from '../../../webmodule-common/interop/interfaces/v6-config';
import { LangFabricationJobActions } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-actions-lang';
import { LangFabricationJobItemsTable } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-items-table-lang';
import { LangFranchiseeQuoteItemsView } from '../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-items-view-lang';
import { LitElementLangBase } from '../../../webmodule-common/other/litelement-base';
import { QuoteApi } from '../../api/quote-api';
import { QuoteItem } from '../../api/dealer-api-interface-quote';
import { quoteItemCellStyles } from '../../../webmodule-common/other/ui/expandable-item-cell-styles';
import { QuoteItemContainer } from '../../quotes/data/quote-item-container';
import { resolveURL } from '../../../webmodule-common/other/ui/resource-resolver';
import { resourceQuote } from '../../quotes/ui/launcher';
import { ViewBase } from '../../../webmodule-common/other/ui/view-base';
import { WebModuleTable, WebModuleTableColumnDef } from '../../../webmodule-common/components/src/webmodule-components';
import type { DeliveryCandidate } from './record-delivery-dialog';

export interface FabricationJobItemsTableOptions {
  containerManager: FabricationJobContainerManager;
  isReadonly: () => boolean;
  onItemClick?: (row: JobItemRow) => Promise<void>;
  onRecordDelivery?: (row: JobItemRow) => Promise<void>;
  onAmendDelivery?: (row: JobItemRow) => Promise<void>;
}

export interface JobItemRow {
  fabricationJobItem: FabricationJobItem;
  quoteItem?: QuoteItem;
  quoteItemContainer?: QuoteItemContainer;
  quoteNumber?: number;
  quoteItemNumber?: number;
  dimensions?: string;
}

/**
 * Styles injected into the <webmodule-table>'s shadow root via its `customStyles`
 * hook: the cell-level layout the page stylesheet used to provide. The table renders
 * cell content inside its shadow DOM, where the global quote-item CSS cannot reach.
 * (Row-extension content styles itself: <wm-fabrication-job-item-expander>.)
 */
const fabricationJobItemsTableStyles = css`
  ${inCellBadgeLinkStyles}

  ${quoteItemCellStyles}

  /* Tighten cell padding to the legacy table's density. The component default (1rem a side)
     is charged against each column's declared width by border-box sizing, so it eats the
     narrow columns first - at 1rem the 48px Qty column had 16px left for its content and
     broke "Qty" across two lines. Scoped to this table. */
  .wm-table {
    --wm-table-cell-padding-x: 0.75rem;
  }

  .quote-item-thumbnail {
    object-fit: contain;
  }

  /* Bootstrap utility used inline by the cell templates. Resolved against the same
     custom property the app's own .text-muted rule uses, so it follows the theme
     instead of pinning a colour from a palette the app does not switch. */
  .text-muted {
    color: var(--bs-secondary-color, var(--webmodule-color-neutral-500, #64748b));
  }
`;

@customElement('wm-fabricationjobitemstable')
export class FabricationJobItemsTable extends ViewBase {
  private options: FabricationJobItemsTableOptions;
  private quoteApi: QuoteApi = getApiFactory().quote();
  @state() private rows: JobItemRow[] = [];
  @state() private loading = false;
  private removing = false;
  @state() private _columns: WebModuleTableColumnDef[] = [];
  @query('#fab-job-items-table') private table?: WebModuleTable;

  constructor(options: FabricationJobItemsTableOptions) {
    super();
    this.options = options;
  }

  get containerManager(): FabricationJobContainerManager {
    return this.options.containerManager;
  }

  get items(): FabricationJobItem[] {
    return this.containerManager.items;
  }

  public async afterConstruction(): Promise<void> {
    this._columns = this.getColumns();
    await this.loadCrossServiceData();
  }

  public async refreshData(): Promise<void> {
    // Rebuild the column set, not just the rows: the delivery-selection column is gated on
    // the job being Completed, and a job can reach Completed while this page is open.
    this._columns = this.getColumns();
    await this.loadCrossServiceData();
  }

  private async loadCrossServiceData(): Promise<void> {
    this.loading = true;
    this.requestUpdate();

    const quoteItemIds = this.items.map(i => i.quoteItemId);
    if (quoteItemIds.length === 0) {
      this.rows = [];
      this.loading = false;
      this.requestUpdate();
      return;
    }

    try {
      const [quoteItemsResult, quoteNumbersResult] = await Promise.all([
        this.quoteApi.getQuoteItemsSummaryById({ quoteItemId: quoteItemIds }),
        this.quoteApi.getQuoteNumbersByQuoteItemIds({ quoteItemIds })
      ]);

      const quoteItemMap = new Map<string, { item: QuoteItem; container: QuoteItemContainer }>();
      if (quoteItemsResult?.items) {
        for (const qi of quoteItemsResult.items) {
          const providerData = quoteItemsResult.data?.find(d => d.id === qi.id);
          const price = quoteItemsResult.prices?.find(p => p.id === qi.id) ?? null;
          quoteItemMap.set(qi.id, {
            item: qi,
            container: { item: qi, data: providerData ?? null, price: price as any }
          });
        }
      }

      // Hydrate provider data from asset paths (V6 frame data is stored as assets)
      await Promise.all(
        Array.from(quoteItemMap.values()).map(async ({ item, container }) => {
          if (container.data && !container.data.providerData) {
            const dataPath = container.data.assetVirtualPaths?.['data'];
            if (dataPath?.path) {
              try {
                const providerData = await fetchQuoteAsset({
                  quoteId: item.quoteId,
                  quoteItemId: item.id,
                  assetPath: dataPath.path,
                  publicUrl: dataPath.publicUrl
                });
                container.data.providerData = providerData as string;
              } catch {
                // Non-blocking — options/dimensions will show as empty
              }
            }
          }
        })
      );

      const quoteNumberMap = new Map<string, number>();
      const itemNumberMap = new Map<string, number>();
      if (quoteNumbersResult?.items) {
        for (const qn of quoteNumbersResult.items) {
          quoteNumberMap.set(qn.quoteItemId, qn.quoteNumber);
          // Why filter on itemNumber > 0? Server returns 0 when the item is
          // not yet in the quote's ItemDisplayOrder (e.g. mid-create); leaving
          // it unset lets the existing truthy guard in the render hide the cell.
          if (qn.itemNumber > 0) itemNumberMap.set(qn.quoteItemId, qn.itemNumber);
        }
      }

      this.rows = this.items.map(fabItem => {
        const quoteData = quoteItemMap.get(fabItem.quoteItemId);
        const quoteNumber = quoteNumberMap.get(fabItem.quoteItemId);
        const quoteItemNumber = itemNumberMap.get(fabItem.quoteItemId);
        let dimensions = '';
        if (quoteData?.container) {
          const v6Item = getV6QuoteItemForContainer(quoteData.container);
          if (v6Item) dimensions = getV6QuoteItemSubtitleOrFrameDimensions(v6Item);
        }
        return {
          fabricationJobItem: fabItem,
          quoteItem: quoteData?.item,
          quoteItemContainer: quoteData?.container,
          quoteNumber,
          quoteItemNumber,
          dimensions
        };
      });
    } catch (e) {
      console.error('Failed to load cross-service data for fabrication job items', e);
      fireQuickErrorToast(LangFabricationJobItemsTable.loadCrossServiceDataFailed());
      this.rows = this.items.map(fabItem => ({ fabricationJobItem: fabItem }));
    }

    this.loading = false;
    this.requestUpdate();
  }

  private getColumns(): WebModuleTableColumnDef[] {
    // Column sizing moves from Bootstrap `colpx-*` / `colpxmax-*` classes (which don't pierce
    // the new table's shadow DOM) to the column's own width model. A pinned `width` is a
    // border-box figure: cell padding comes out of it before the content, so each of the narrow
    // columns below is its header label plus the 1.5rem of padding, not the legacy colpx value.
    const cols: WebModuleTableColumnDef[] = [];

    // Both delivery columns key off the same fact, so derive it once: an item can only be
    // Ready for Delivery or Delivered once its job has completed.
    const isCompleted = this.containerManager.fabricationJob.state === FabricationJobState.Completed;

    // Item #
    cols.push({
      title: () => LangFabricationJobItemsTable.itemNumberTitle(),
      width: '56px',
      fieldName: 'id',
      displayValue: (_table: WebModuleTable, _item: unknown, index: number) => {
        return html`${index + 1}`;
      }
    });

    // Quote # + Quote Item Id
    cols.push({
      title: () => LangFabricationJobItemsTable.quoteNumberTitle(),
      width: '90px',
      classes: 'quote-item-title',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const row = item as JobItemRow;
        const qi = row.quoteItem;
        const quoteLink = row.quoteNumber
          ? html`<a class="quote-item-link" href="${resolveURL(resourceQuote, qi?.quoteId ?? '')}"
              >${LangFabricationJobItemsTable.quoteNumberLink(row.quoteNumber)}</a
            >`
          : html`<span>-</span>`;
        const itemNum = row.quoteItemNumber
          ? html`<span class="quote-item-description text-muted"
              >${LangFabricationJobItemsTable.itemNumberLabel(row.quoteItemNumber)}</span
            >`
          : html``;
        return html`${quoteLink}${itemNum}`;
      }
    });

    // Image
    cols.push({
      title: () => LangFabricationJobItemsTable.imageTitle(),
      width: '80px',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const row = item as JobItemRow;
        const qi = row.quoteItem;
        if (!qi) return html``;

        const src =
          qi.virtualThumbnailPath === ''
            ? quoteItemThumbSrc(qi)
            : this.quoteApi.convertQuoteItemAssetPathToUrl(
                qi.quoteId,
                qi.id,
                qi.virtualThumbnailPath,
                // Content hash, so the URL changes exactly when the image does - see assetContentTag.
                assetContentTag(row.quoteItemContainer?.data?.assetVirtualPaths, 'thumbnail', qi.recordVersion)
              );

        return html`<img class="quote-item-thumbnail" alt="" src="${src}" style="width:48px; height:48px" />`;
      }
    });

    // Location / Description
    cols.push({
      title: () => LangFabricationJobItemsTable.locationDescriptionTitle(),
      minWidth: '240px',
      classes: 'quote-item-title',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const row = item as JobItemRow;
        const qi = row.quoteItem;
        const title = qi?.title || row.fabricationJobItem.quoteItemId.substring(0, 8);
        const clickEvent = (e: Event) => {
          e.stopPropagation();
          e.preventDefault();
          this.options.onItemClick?.(row);
        };
        return html`<a class="quote-item-link" href="#" @click=${clickEvent}>${title}</a>
          <span class="quote-item-description">${qi?.description ?? ''}</span>`;
      }
    });

    // Options (dimensions + expand/collapse)
    const expandButton = html`
      <span>${LangFabricationJobItemsTable.optionsTitle()}</span>
      <div>
        <webmodule-icon
          id="fab-job-expand-all-indicator"
          library="fa"
          name="fas-angles-down"
          @click=${() => this.toggleAllExpanded()}
        ></webmodule-icon>
      </div>
    `;

    cols.push({
      title: html`${expandButton}`,
      minWidth: '160px',
      classes: 'quote-item-description',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const row = item as JobItemRow;
        if (!row.quoteItemContainer) return html`${row.dimensions || ' - '}`;

        const oneLiner = this.getOptionsOneLiner(row.quoteItemContainer);
        const isExpanded = _table.isExpanded(row) != undefined;
        // webmodule-icon rather than a raw <i>: Font Awesome's class rules do not
        // reach the table's shadow DOM. Bootstrap's float-end is gone for the same
        // reason - the caret is pushed right by the customStyles rule below.
        return html`<span>${oneLiner}</span
          ><span class="options-dropdown"
            ><webmodule-icon library="fa" name=${isExpanded ? 'fas-caret-up' : 'fas-caret-down'}></webmodule-icon
          ></span>`;
      },
      click: (e: Event, table: WebModuleTable, item: unknown) => {
        e.stopPropagation();
        e.preventDefault();
        const row = item as JobItemRow;
        if (!row.quoteItemContainer) return false;

        if (table.isExpanded(row)) {
          table.remExtension(row);
        } else {
          table.addExtension(row, this.extendedDetailsGenerator(row));
        }
        return true;
      }
    });

    // Site Overrides
    cols.push({
      title: () => LangFabricationJobItemsTable.siteOverridesTitle(),
      minWidth: '200px',
      fade: true,
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const row = item as JobItemRow;
        const quantity = row.quoteItem?.quantity ?? 0;
        const overrideCount =
          row.fabricationJobItem.unitOverrides?.filter(o => o.v6QuoteItemVirtualPath !== null).length ?? 0;
        const clickEvent = (e: Event) => {
          e.stopPropagation();
          e.preventDefault();
          this.options.onItemClick?.(row);
        };
        if (quantity <= 0) return html` - `;
        if (overrideCount === 0)
          return html`<a class="quote-item-link text-muted" href="#" @click=${clickEvent}
            >${LangFabricationJobItemsTable.noOverrides()}</a
          >`;
        return html`<a class="quote-item-link" href="#" @click=${clickEvent}
          >${LangFabricationJobItemsTable.unitsHaveOverrides(overrideCount, quantity)}</a
        >`;
      }
    });

    // Tags
    cols.push({
      title: () => LangFabricationJobItemsTable.tagsTitle(),
      width: '80px',
      classes: 'quote-item-tags',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const row = item as JobItemRow;
        if (!row.quoteItemContainer) return html``;
        return this.getItemTags(row.quoteItemContainer);
      }
    });

    // Qty
    cols.push({
      title: () => LangFabricationJobItemsTable.qtyTitle(),
      width: '60px',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const row = item as JobItemRow;
        return html`${row.quoteItem?.quantity ?? ' - '}`;
      }
    });

    // Factory tracking reference. The cell is the edit affordance - same dialog the
    // row menu opens, one click closer. A blank reference still renders a link so the
    // first value can be entered from here too; the dash is the only thing to aim at.
    cols.push({
      title: () => LangFabricationJobActions.trackingReferenceColumnTitle(),
      width: '120px',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const row = item as JobItemRow;
        const reference = this.containerManager.itemFabrication(
          row.fabricationJobItem.quoteItemId
        )?.factoryTrackingReference;
        const display = reference || ' - ';
        if (!this.canEditTrackingReference) return html`${display}`;
        return html`<a
          href="#"
          class="quote-item-link"
          title=${LangFabricationJobActions.trackingReferenceDialogTitle()}
          @click=${(e: Event) => {
            e.stopPropagation();
            e.preventDefault();
            void this.editTrackingReference(row);
          }}
          >${display}</a
        >`;
      }
    });

    // Delivery state. Same Completed-only gate as the selection column: Delivered is only
    // reachable once the job completes. A Delivered row's status is the amend affordance.
    if (isCompleted) {
      cols.push({
        title: () => LangFranchiseeQuoteItemsView.fabricationStatus(),
        width: '140px',
        fieldName: 'xx',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const row = item as JobItemRow;
          const fab = this.containerManager.itemFabrication(row.fabricationJobItem.quoteItemId);
          if (!fab) return html``;
          if (fab.fabricationStatus === QuoteItemFabricationStatus.ReadyForDelivery)
            // Opens the delivery checklist for the whole job, with this item pre-ticked.
            return html`<a
              href="javascript:void(0)"
              class="badge badge-success"
              title=${LangFabricationJobActions.deliveryDialogTitle()}
              @click=${(e: Event) => {
                e.stopPropagation();
                e.preventDefault();
                void this.options.onRecordDelivery?.(row);
              }}
              ><webmodule-icon
                library="fa"
                name=${getQuoteItemFabricationStatusIcon(QuoteItemFabricationStatus.ReadyForDelivery)}
              ></webmodule-icon>
              ${LangFranchiseeQuoteItemsView.readyForDelivery()}</a
            >`;
          if (fab.fabricationStatus === QuoteItemFabricationStatus.Delivered)
            return html`<a
              href="javascript:void(0)"
              class="badge badge-secondary"
              title=${LangFabricationJobActions.deliveryViewHint()}
              @click=${(e: Event) => {
                e.stopPropagation();
                e.preventDefault();
                this.options.onAmendDelivery?.(row);
              }}
              ><webmodule-icon
                library="fa"
                name=${getQuoteItemFabricationStatusIcon(QuoteItemFabricationStatus.Delivered)}
              ></webmodule-icon>
              ${LangFranchiseeQuoteItemsView.delivered()}</a
            >`;
          return html``;
        }
      });
    }

    // Actions (hamburger menu)
    cols.push({
      title: () => '',
      width: '64px',
      fieldName: 'xx',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const row = item as JobItemRow;
        const isReadonly = this.options.isReadonly() || !this.containerManager.isDraft;
        const clickView = (e: Event) => {
          e.stopPropagation();
          this.options.onItemClick?.(row);
        };
        const clickDelete = (e: Event) => {
          e.stopPropagation();
          this.removeItem(row);
        };
        const clickTrackingReference = (e: Event) => {
          e.stopPropagation();
          void this.editTrackingReference(row);
        };
        return html`<webmodule-dropdown class="item-menu text-start" placement="bottom-end" hoist>
          <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
          <webmodule-menu>
            <webmodule-menu-item @click=${clickView}>
              <webmodule-icon slot="prefix" library="fa" name="fas-pen-to-square"></webmodule-icon>
              ${LangFabricationJobItemsTable.editMenuItem()}
            </webmodule-menu-item>
            ${this.canEditTrackingReference
              ? html`<webmodule-menu-item @click=${clickTrackingReference}>
                  <webmodule-icon slot="prefix" library="fa" name="fas-tag"></webmodule-icon>
                  ${LangFabricationJobActions.trackingReferenceMenuItem()}
                </webmodule-menu-item>`
              : html``}
            ${!isReadonly
              ? html`<webmodule-menu-item @click=${clickDelete}>
                  <webmodule-icon slot="prefix" library="fa" name="fas-trash"></webmodule-icon>
                  ${LangFabricationJobItemsTable.removeMenuItem()}
                </webmodule-menu-item>`
              : html``}
          </webmodule-menu>
        </webmodule-dropdown>`;
      }
    });

    return cols;
  }

  /**
   * Why not containerManager.isReadOnly()? That is isLockedFromUse() || !isDraft, so it is
   * unconditionally true once the job leaves Draft - the very point at which the tracking
   * reference becomes meaningful. This affordance wants the Draft term inverted (the fabricator
   * has nothing to track until the job is real) and the lock term kept as-is, since another
   * session owning the record blocks this edit as it blocks every other.
   */
  private get canEditTrackingReference(): boolean {
    return !this.containerManager.isDraft && !this.containerManager.isLockedFromUse();
  }

  /**
   * Prompt for the item's factory tracking reference and store the answer. Shared by the
   * tracking reference cell and the row menu so both behave identically.
   */
  private async editTrackingReference(row: JobItemRow): Promise<void> {
    const quoteItemId = row.fabricationJobItem.quoteItemId;
    const current = this.containerManager.itemFabrication(quoteItemId)?.factoryTrackingReference ?? '';
    const next = await FactoryTrackingReferenceDialog.Execute(current);
    if (next === null) return;
    await this.containerManager.setFactoryTrackingReference(quoteItemId, next);
    // Repaint only. setFactoryTrackingReference reloads the job from the server, and the cell
    // reads the reference straight off the container manager, so the value is already current -
    // refreshData() would re-fetch every item's quote summary and thumbnail (and flash the
    // table's loading state) to show one changed string.
    this.table?.requestUpdate();
  }

  /**
   * The job's Ready-for-Delivery items as checklist rows, built from the same row data the
   * table renders so the dialog shows what the user is looking at.
   */
  public deliveryCandidates(): DeliveryCandidate[] {
    return this.candidatesWhere(
      id => this.containerManager.itemFabrication(id)?.fabricationStatus === QuoteItemFabricationStatus.ReadyForDelivery
    );
  }

  /**
   * The items of one recorded delivery. Falls back to the clicked item alone when the batch id is
   * missing, so a row delivered before batch ids existed still shows something truthful.
   */
  public deliveredCandidates(batchId: string | null | undefined, clickedQuoteItemId: string): DeliveryCandidate[] {
    return this.candidatesWhere(id =>
      isSameDeliveryBatch(this.containerManager.itemFabrication(id)?.deliveryBatchId, id, batchId, clickedQuoteItemId)
    );
  }

  private candidatesWhere(include: (quoteItemId: string) => boolean): DeliveryCandidate[] {
    return this.rows
      .filter(row => include(row.fabricationJobItem.quoteItemId))
      .map(row => {
        const qi = row.quoteItem;
        return {
          quoteItemId: row.fabricationJobItem.quoteItemId,
          itemNumber: row.quoteItemNumber !== undefined ? String(row.quoteItemNumber) : '',
          thumbnailUrl:
            qi === undefined
              ? undefined
              : qi.virtualThumbnailPath === ''
                ? quoteItemThumbSrc(qi)
                : this.quoteApi.convertQuoteItemAssetPathToUrl(
                    qi.quoteId,
                    qi.id,
                    qi.virtualThumbnailPath,
                    assetContentTag(row.quoteItemContainer?.data?.assetVirtualPaths, 'thumbnail', qi.recordVersion)
                  ),
          location: qi?.title || row.fabricationJobItem.quoteItemId.substring(0, 8),
          description: qi?.description,
          options: row.quoteItemContainer ? this.getOptionsOneLiner(row.quoteItemContainer) : row.dimensions,
          tags: row.quoteItemContainer ? this.getItemTags(row.quoteItemContainer) : undefined,
          trackingReference:
            this.containerManager.itemFabrication(row.fabricationJobItem.quoteItemId)?.factoryTrackingReference ?? '',
          quantity: qi?.quantity
        };
      });
  }

  private getOptionsOneLiner(container: QuoteItemContainer): string {
    if (!isV6(container)) return '';
    const data = getV6QuoteItemForContainer(container);
    return getV6QuoteItemSubtitleOrFrameDimensions(data);
  }

  private extendedDetailsGenerator(row: JobItemRow) {
    return async () => {
      const container = row.quoteItemContainer;
      if (!container) return html``;
      return html`<wm-fabrication-job-item-expander .container=${container}></wm-fabrication-job-item-expander>`;
    };
  }

  private getItemTags(container: QuoteItemContainer): TemplateResult {
    const validationIndicator = () => {
      if (!isV6(container)) return html``;
      const data = getV6FrameForContainer(container);
      if (!data) return html``;
      if (!data.validations?.length) return html``;

      const rank = highestValidationRank(data.validations);
      return frameValidationIcon(rankToValidationType(rank)) ?? html``;
    };

    const notesIndicator = () => {
      if (container.item.comment)
        return html`<webmodule-icon library="fa" name="fas-message" color="green"></webmodule-icon>`;
      return html``;
    };

    const notesManufacturerIndicator = () => {
      if (container.item.commentToManufacturer)
        return html`<webmodule-icon library="fa" name="fas-message" color="black"></webmodule-icon>`;
      return html``;
    };

    return html`${notesIndicator()}${notesManufacturerIndicator()}${validationIndicator()}`;
  }

  private toggleAllExpanded() {
    if (!this.table) return;
    const anyExpanded = this.rows.some(row => this.table!.isExpanded(row));
    this.rows.forEach(row => {
      if (anyExpanded) {
        if (this.table!.isExpanded(row)) this.table!.remExtension(row);
      } else {
        if (!this.table!.isExpanded(row) && row.quoteItemContainer) {
          this.table!.addExtension(row, this.extendedDetailsGenerator(row));
        }
      }
    });
  }

  private async removeItem(row: JobItemRow): Promise<void> {
    if (this.removing) return;
    this.removing = true;
    try {
      await this.containerManager.removeItem(row.fabricationJobItem.quoteItemId);
      await this.loadCrossServiceData();
    } finally {
      this.removing = false;
    }
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  protected template(): EventTemplate {
    if (this.loading) {
      return html`<div class="p-3 text-muted">${LangFabricationJobItemsTable.loadingItems()}</div>`;
    }

    // The element's own defaults are its standalone-widget look - drop shadow, cell borders,
    // UPPERCASE headers - so a direct consumer turns them off to sit flat on the page, the way
    // DataTableWrapper does for every wrapper-based table. Every other direct <webmodule-table>
    // consumer in the app hard-codes the same three.
    //
    // No pageLength is set, and the component's default for it is irrelevant here: the
    // paginator is gated on pageCount = ceil(rowCount / pageLength), and this table never
    // sets rowCount - it binds .data directly and handles no fetch event - so rowCount stays
    // 0, pageCount is 0, and showPager (pageCount > 1) is false at any item count. The table
    // never slices .data by page either, so every row renders and the body scrolls. Being a
    // direct consumer rather than a DataTableWrapper subclass, disablePagination() is not
    // reachable from here.
    return html`<webmodule-table
      id="fab-job-items-table"
      .customStyles=${fabricationJobItemsTableStyles}
      .columns=${this._columns}
      .data=${this.rows}
      .clickrows=${false}
      .keyevent=${(row: JobItemRow) => row.fabricationJobItem.quoteItemId}
      .rowidevent=${(row: JobItemRow) => row.fabricationJobItem.quoteItemId}
      .elevated=${false}
      .cellBorders=${false}
      .uppercaseHeader=${false}
      tablestyle="nestedtable"
    ></webmodule-table>`;
  }
}

/**
 * Row-extension body shown when a job item row is expanded: the frame option groups,
 * validation messages and notes. The host <webmodule-table> renders extension content
 * inside ITS shadow DOM, where the page stylesheet cannot reach, so the component
 * carries its own styles (same pattern as <wm-purchase-order-item-expander> and
 * <franchisee-quote-item-expander>). The colours resolve against the app's inherited
 * custom properties declared on :root, which do cross the shadow boundary.
 */
@customElement('wm-fabrication-job-item-expander')
export class FabricationJobItemExpander extends LitElementLangBase {
  static styles = frameExpanderStyles;

  @property({ attribute: false })
  public container?: QuoteItemContainer;

  protected render(): TemplateResult {
    if (!this.container) return html``;
    return this.getExtendedDetails(this.container);
  }

  private getExtendedDetails(container: QuoteItemContainer): TemplateResult {
    if (!isFrame(container.item) || !isV6(container)) {
      return this.getNotesTemplate(container);
    }

    if (!isValidV6ConfigVersion()) return html``;

    const data = getV6FrameForContainer(container);

    const validationsTemplate = (validationType: FrameValidationType, cssClass: string): TemplateResult => {
      const items = data?.validations?.filter(x => x.type === validationType) ?? [];
      if (items.length === 0) return html``;
      return html`
        <ul class="list-group">
          ${items.map(
            issue =>
              html`<li class="list-group-item ${cssClass}">
                <span class="me-3 fw-bold">${interpolateExternal(issue.title)}</span>${interpolateExternal(
                  issue.text ?? ''
                )}
              </li>`
          )}
        </ul>
      `;
    };

    return html`<div class="extended-options-line small-font main-body">
      ${data ? getFrameDetailsTemplate(data, true) : nothing}
      ${validationsTemplate(FrameValidationType.critical, 'list-group-item-danger')}
      ${validationsTemplate(FrameValidationType.warning, 'list-group-item-warning')}
      ${validationsTemplate(FrameValidationType.information, 'list-group-item-info')}
      ${validationsTemplate(FrameValidationType.note, 'list-group-item-note')} ${this.getNotesTemplate(container)}
    </div>`;
  }

  private getNotesTemplate(container: QuoteItemContainer): TemplateResult {
    const hasComment = !isEmptyOrSpace(container.item.comment);
    const hasManufacturerComment = !isEmptyOrSpace(container.item.commentToManufacturer);
    if (!hasComment && !hasManufacturerComment) return html``;

    return html`<ul class="list-group small-font">
      ${hasComment
        ? html`<li class="list-group-item list-group-item-primary">
            <span class="me-3 fw-bold">${LangFabricationJobItemsTable.notesToClient()}</span> ${container.item.comment}
          </li>`
        : nothing}
      ${hasManufacturerComment
        ? html`<li class="list-group-item list-group-item-primary">
            <span class="me-3 fw-bold">${LangFabricationJobItemsTable.notesToManufacturer()}</span> ${container.item
              .commentToManufacturer}
          </li>`
        : nothing}
    </ul>`;
  }
}
